import React, { useMemo } from 'react'
import { Divider, H2, IconButton, List, Row, Separator } from '@fs/zion-ui'
import { NoticeInfo } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import PersonDisplay from './PersonDisplay'
import { sortPersonsByPrimary } from '../utils/display.utils'

/**
 * FloatingPersonsList
 *
 * Renders persons that are not attached to any group ("floating" persons) as a list, under a
 * "Names not in a group" heading with an info control explaining how to move them.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.persons - Array of floating person objects to display
 * @param {Function} [props.onPersonSelect] - Callback when a person in the list is selected
 * @returns {JSX.Element|null} The floating persons list, or null when there are no persons
 */
const FloatingPersonsList = ({ persons = [], onPersonSelect }) => {
  const [t] = useTranslation()

  const sortedPersons = useMemo(() => sortPersonsByPrimary(persons), [persons])

  if (sortedPersons.length === 0) return null

  const heading = t('household-list.names-not-in-group', 'Names not in a group')

  return (
    <section aria-label={heading} data-testid="names-not-in-a-group">
      <Row alignY="middle" guttersY="pico">
        <H2 size="H6" growX>
          {heading}
        </H2>
        <IconButton
          Icon={NoticeInfo}
          size="xs"
          aria-label={t('household-list.names-not-in-group.info-label', 'About names not in a group')}
          description={t(
            'household-list.names-not-in-group.info-description',
            'To add to an existing group, open a person in that group and edit Relationships. To start a new group, use New group.'
          )}
          data-testid="names-not-in-a-group-info"
        />
      </Row>
      <Separator size="nano" />
      <List>
        {sortedPersons.map((person) => (
          <PersonDisplay key={person?.id} person={person} onPersonSelect={onPersonSelect} />
        ))}
      </List>
      <Divider marginY="xxs" fullWidth />
    </section>
  )
}

export default FloatingPersonsList
